import { useEffect, useRef } from 'react';

import { useStore, useStoreApi } from '@xyflow/react';

import { FlowsInput, FlowsOutput } from 'hooks/data/datasets/V4/flows/types';

type Params = {
  inputs: (FlowsInput | FlowsOutput)[];
  outputs: (FlowsInput | FlowsOutput)[];
};

// React Flow's nodesInitialized never becomes true in controlled mode, so fitView never fires.
export const useAutoLayout = ({ inputs, outputs }: Params) => {
  const storeApi = useStoreApi();
  const isDatasetMeasured = useStore((s) => {
    const node = s.nodeLookup?.get('dataset');
    return !!(node?.measured?.width && node?.measured?.height);
  });
  const layoutKeyRef = useRef<string | null>(null);

  useEffect(() => {
    if (!isDatasetMeasured) return;

    const key = [...inputs, ...outputs].map((f) => f.id).join(',');
    if (key === layoutKeyRef.current) return;

    const timerId = setTimeout(() => {
      const currentKey = [...inputs, ...outputs].map((f) => f.id).join(',');
      if (currentKey === layoutKeyRef.current) return;
      layoutKeyRef.current = currentKey;

      const { panZoom, nodeLookup, domNode } = storeApi.getState();
      if (!panZoom || !domNode || !nodeLookup) return;

      const datasetNode = nodeLookup.get('dataset');
      if (!datasetNode?.measured?.width || !datasetNode?.measured?.height) return;

      const containerWidth = domNode.offsetWidth;
      const containerHeight = domNode.offsetHeight;
      if (!containerWidth || !containerHeight) return;

      const { positionAbsolute } = datasetNode.internals;
      const { width, height } = datasetNode.measured;

      const zoom = 1;
      const x = containerWidth / 2 - (positionAbsolute.x + width / 2) * zoom;
      const y = containerHeight / 2 - (positionAbsolute.y + height / 2) * zoom;

      panZoom.setViewport({ x, y, zoom }, { duration: 600 });
    }, 0);

    return () => clearTimeout(timerId);
  }, [isDatasetMeasured, storeApi, inputs, outputs]);
};
